import React, { useRef } from 'react';

interface Props {
  /** Accessible name for the scroll region. */
  label: string;
  children: React.ReactNode;
  className?: string;
  /** Extra classes for the scrolling track (gap, padding). */
  trackClassName?: string;
}

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Horizontal scroller used ONLY where sideways movement helps (card rails,
 * wide comparison tables). Cards render once, in order. Drag, touch, and the
 * on-brand scrollbar move the track. Arrow keys scroll it when it is focused.
 */
const HScroller: React.FC<Props> = ({ label, children, className = '', trackClassName = '' }) => {
  const ref = useRef<HTMLDivElement>(null);

  const onKeyDown = (e: React.KeyboardEvent) => {
    const el = ref.current;
    if (!el) return;
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const dir = e.key === 'ArrowRight' ? 1 : -1;
    const behavior: ScrollBehavior = prefersReducedMotion() ? 'auto' : 'smooth';
    el.scrollBy({ left: dir * Math.max(160, el.clientWidth * 0.8), behavior });
  };

  return (
    <div className={className}>
      <div
        ref={ref}
        role="region"
        aria-label={label}
        tabIndex={0}
        onKeyDown={onKeyDown}
        className={`fc-hscroll flex overflow-x-auto snap-x snap-mandatory scroll-px-4 pb-3 rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 ${trackClassName}`}
      >
        {children}
      </div>
    </div>
  );
};

export default HScroller;
